<template>
  <div class="max-w-xl mx-auto py-12 px-4">
    <!-- Progress Indicator -->
    <div class="flex items-center justify-center gap-4 mb-12 text-xs font-semibold text-gray-400">
      <div class="flex items-center gap-1.5 text-gray-900">
        <span class="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-[10px] font-bold">
          <i class="fa-solid fa-check text-[8px]"></i>
        </span>
        <span>Connect</span>
      </div>
      <div class="w-12 h-0.5 bg-gray-900"></div>
      <div class="flex items-center gap-1.5 text-gray-950">
        <span class="w-6 h-6 rounded-full border-2 border-gray-950 text-gray-950 flex items-center justify-center text-[10px] font-extrabold">2</span>
        <span class="font-extrabold">Select</span>
      </div>
      <div class="w-12 h-0.5 bg-gray-200"></div>
      <div class="flex items-center gap-1.5 text-gray-300">
        <span class="w-6 h-6 rounded-full border-2 border-gray-200 text-gray-300 flex items-center justify-center text-[10px] font-bold">3</span>
        <span>Dashboard</span>
      </div>
    </div>

    <!-- Main Card -->
    <div class="bg-white rounded-2xl border border-gray-150 shadow-sm p-8 text-center">
      <h1 class="text-3xl font-extrabold tracking-tight text-gray-900">Pick a Property</h1>
      <p class="text-sm text-gray-500 max-w-sm mx-auto mt-3 mb-8 leading-relaxed">
        Your site stays anonymous by default (shown as a fun fake name). You can reveal the real domain later in settings.
      </p>

      <div v-if="properties.length === 0" class="text-center py-6 text-sm text-gray-400">
        No Search Console properties found for your Google account.
      </div>

      <div v-else class="space-y-3 mb-8 text-left">
        <div v-if="properties.length > 0" class="text-right text-[10px] font-extrabold tracking-wider text-gray-400 uppercase mb-2">
          Select One
        </div>
        <div 
          v-for="prop in properties" 
          :key="prop.siteUrl"
          @click="selectedUrl = prop.siteUrl"
          :class="`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-all ${selectedUrl === prop.siteUrl ? 'border-gray-900 ring-1 ring-gray-900 bg-gray-50/20' : 'border-gray-200 hover:border-gray-300 bg-white'}`"
        >
          <div class="flex items-center gap-3">
            <div class="flex items-center justify-center">
              <span 
                :class="`w-5 h-5 rounded-md border flex items-center justify-center text-white ${selectedUrl === prop.siteUrl ? 'bg-gray-900 border-gray-900' : 'border-gray-300 bg-white'}`"
              >
                <i v-if="selectedUrl === prop.siteUrl" class="fa-solid fa-check text-[10px]"></i>
              </span>
            </div>
            <div>
              <p class="text-sm font-bold text-gray-900">{{ displaySiteUrl(prop.siteUrl) }}</p>
              <p class="text-[10px] text-gray-400 font-medium tracking-wide mt-0.5 uppercase">
                {{ prop.siteUrl.startsWith('sc-domain:') ? 'Domain Property' : 'URL Property' }}
              </p>
            </div>
          </div>

          <span class="text-xs text-gray-400 font-extrabold uppercase tracking-wider">
            {{ prop.permissionLevel === 'siteOwner' ? 'Owner' : 'Member' }}
          </span>
        </div>
      </div>

      <!-- Action Button -->
      <button 
        @click="verifyProperty"
        :disabled="!selectedUrl || verifying"
        :class="`w-full py-4 text-sm font-bold rounded-xl text-white transition-all shadow-sm ${!selectedUrl || verifying ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-700 hover:bg-gray-800 active:scale-[0.98]'}`"
      >
        {{ verifying ? 'Pulling traffic data...' : 'Verify this property' }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PropertySelectorStep',
  props: {
    initialProperties: {
      type: Array,
      default: () => []
    },
    googleAccountId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      properties: this.initialProperties,
      selectedUrl: null,
      verifying: false
    };
  },
  methods: {
    displaySiteUrl(url) {
      if (!url) return '';
      return url.replace(/^https?:\/\//i, '').replace(/^sc-domain:/i, '').replace(/\/$/, '');
    },
    async verifyProperty() {
      if (!this.selectedUrl || this.verifying) return;
      
      const selectedProp = this.properties.find(p => p.siteUrl === this.selectedUrl);
      if (!selectedProp) return;

      this.verifying = true;
      try {
        const response = await axios.post('/properties/select', {
          google_account_id: this.googleAccountId,
          site_url: selectedProp.siteUrl,
          permission_level: selectedProp.permissionLevel
        });

        if (response.data.success) {
          // Redirect to dashboard (Step 3)
          window.location.href = '/dashboard';
        } else {
          alert('Failed to select property.');
        }
      } catch (err) {
        alert('Error selecting property: ' + (err.response?.data?.message || err.message));
      } finally {
        this.verifying = false;
      }
    }
  }
};
</script>
